import React, { useEffect, useState, Component } from 'react';
import { motion } from 'framer-motion';
import { Wind, Rocket, ShieldCheck, Send, CheckCircle2, HelpCircle } from 'lucide-react';
import { UtmCapture } from '../components/UtmCapture';
import { getUtmParams } from '../utils/utmTracking';
import { sendWingFoilLeadToMake } from '../utils/makeWebhook';
export function WingFoilCampaignPage() {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  useEffect(() => {
    document.title = "Wing Foil - בואו לעוף על המים | ביץ' קלאב מעגן";
  }, []);
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.phone.trim()) return;
    setIsSubmitting(true);
    // Capture UTM params at submission time
    const utmParams = getUtmParams();
    // Track Meta Pixel custom event for this landing page's lead
    if (typeof window !== 'undefined' && (window as any).fbq) {
      ;
      (window as any).fbq('trackCustom', 'Lead_WingFoil_LP', {
        content_name: 'Wing Foil Campaign',
        currency: 'ILS'
      });
    }
    // Send the lead to Make.com (name/phone/email + UTM attribution).
    // We don't block the user experience on webhook success — if it fails
    // we still show the success state and open WhatsApp.
    await sendWingFoilLeadToMake({
      type: 'ליד Wing Foil',
      source: 'טופס קמפיין ווינג פויל',
      campaign: 'Wing Foil Campaign',
      fullName: formData.fullName.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim(),
      submittedAt: new Date().toISOString(),
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_content: utmParams.utm_content,
      utm_term: utmParams.utm_term,
      utm_id: utmParams.utm_id,
      fbclid: utmParams.fbclid
    });
    setIsSubmitting(false);
    setIsSubmitted(true);
    // Redirect to WhatsApp after successful submission
    const text = encodeURIComponent(`היי, השארתי פרטים בדף הקמפיין של הווינג פויל.\nשם: ${formData.fullName}\nטלפון: ${formData.phone}`);
    window.open(`https://wa.me/972522632784?text=${text}`, '_blank');
  };
  return <div className="min-h-screen bg-white" dir="rtl" data-id="element-2607" data-mp="30li8lock9jlh">
      <UtmCapture data-id="element-2608" data-mp="3edeu39jq4v8w" />

      {/* Minimal Header */}
      <header className="absolute top-0 left-0 right-0 z-50 p-6" data-id="element-2609" data-mp="3ef9g6r6qetek">
        <div className="container mx-auto flex justify-center md:justify-start" data-id="element-2610" data-mp="39fdc6ry1b1z0">
          <img src="https://cdn.magicpatterns.com/uploads/bHRRxzcgoWuLZVsSMLwyGt/logo.png" alt="Beach Club Logo" className="h-12 md:h-16 brightness-0 invert drop-shadow-[0_2px_10px_rgba(0,0,0,0.85)]" data-id="element-2611" data-mp="3oajlbnsffgwk" />
        </div>
      </header>

      {/* Hero Section with Form */}
      <section className="relative min-h-[90vh] flex items-center pt-24 pb-12" data-id="element-2612" data-mp="au8ycvd6lqvz">
        <div className="absolute inset-0 z-0" data-id="element-2613" data-mp="as4w0huxkzxt">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/co1SZT3RTYumCS8rU85uzt/IMG-20250502-WA0017.jpg" alt="Wing Foil" className="w-full h-full object-cover" style={{
          objectPosition: '60% 40%'
        }} data-id="element-2614" data-mp="3t0nbwt7jfytu" />
          <div className="absolute inset-0 bg-gradient-to-l from-black/90 via-black/60 to-black/20" data-id="element-2615" data-mp="3j8hm9q4rdtht"></div>
          {/* Keeps the white logo legible against the bright sky on mobile */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/70 to-transparent md:hidden" aria-hidden="true" data-id="element-2616" data-mp="3aanqqhetj3vp"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-2617" data-mp="30b6m83bayw1b">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-2618" data-mp="30bq2lflb1cd8">
            {/* Text Content */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} className="text-white" data-id="element-2619" data-mp="359hwug9f9akl">
              <div className="inline-flex items-center gap-2 bg-cyan-500/20 border border-cyan-400/30 px-4 py-1.5 rounded-full mb-6 backdrop-blur-sm" data-id="element-2620" data-mp="2pzrho6gygag">
                <Wind className="w-4 h-4 text-cyan-300" data-id="element-2621" data-mp="3tvhmklkcgvkp" />
                <span className="text-cyan-100 text-sm font-medium" data-id="element-2622" data-mp="3owy4wxcsa5y5">
                  הספורט הימי שמשגע את העולם
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-extrabold mb-6 leading-tight" data-id="element-2623" data-mp="3kub78ftflqt1">
                בואו לעוף <br data-id="element-2624" data-mp="3fwrtdh0nbg1m" />
                <span className="text-cyan-400" data-id="element-2625" data-mp="3fycmd27baqxd">על המים</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-lg leading-relaxed font-light" data-id="element-2626" data-mp="3az9g5eflient">
                הצטרפו אלינו לביץ' קלאב מעגן ותגלו את תחושת הריחוף הממכרת של
                ה-Wing Foil. הדרכות למתחילים, ציוד חדיש וחוף מושלם.
              </p>

              <div className="space-y-4 hidden md:block" data-id="element-2627" data-mp="1gojiwzlxck6i">
                <div className="flex items-center gap-3" data-id="element-2628" data-mp="1lony8mb3j8v5">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" data-id="element-2629" data-mp="1ln34v00ipvyv" />
                  <span className="text-lg" data-id="element-2630" data-mp="1qf4d8aukf2jm">ציוד קל ובטיחותי ללמידה מהירה</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-2631" data-mp="1veqqgkae8isf">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" data-id="element-2632" data-mp="1vgbju6kz1vop" />
                  <span className="text-lg" data-id="element-2633" data-mp="xs9upjhb6d8s">הדרכה מקצועית צמודה</span>
                </div>
                <div className="flex items-center gap-3" data-id="element-2634" data-mp="12ql6w0v2szu6">
                  <CheckCircle2 className="w-6 h-6 text-cyan-400" data-id="element-2635" data-mp="17p68a3m2dpk7" />
                  <span className="text-lg" data-id="element-2636" data-mp="17qzexiwog9s7">תנאי רוח אידיאליים בכנרת</span>
                </div>
              </div>
            </motion.div>

            {/* Lead Form */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="bg-white/10 backdrop-blur-md border border-white/20 p-8 rounded-3xl shadow-2xl max-w-md w-full mx-auto md:mx-0" data-id="element-2637" data-mp="2j3a0p5d075n5">
              {isSubmitted ? <div className="text-center py-8" data-id="element-2638" data-mp="x0ju5x7xpqa6">
                  <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2639" data-mp="wz8plv6o0ei0">
                    <CheckCircle2 className="w-8 h-8 text-emerald-400" data-id="element-2640" data-mp="5a5jsaz4qw39" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2" data-id="element-2641" data-mp="bklwi7coohz">
                    איזה כיף!
                  </h3>
                  <p className="text-gray-200" data-id="element-2642" data-mp="e3r1a22oirgd">
                    הפרטים התקבלו בהצלחה. נחזור אלייך בהקדם עם כל המידע.
                  </p>
                </div> : <>
                  <h3 className="text-2xl font-bold text-white mb-2 text-center" data-id="element-2643" data-mp="3hrkau4iiw9rc">
                    רוצה לשמוע עוד?
                  </h3>
                  <p className="text-gray-300 text-center mb-6 text-sm" data-id="element-2644" data-mp="3dpflvj30s1b1">
                    השאירו פרטים ונחזור אליכם עם כל המידע על שיעורי ההיכרות שלנו
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2645" data-mp="38d8qhq7vys24">
                    <div data-id="element-2646" data-mp="2to4a8eknixwn">
                      <input type="text" name="fullName" value={formData.fullName} onChange={handleFormChange} required placeholder="שם מלא" className="w-full bg-white/90 border-0 rounded-xl py-3 px-4 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-cyan-400 transition-all" data-id="element-2647" data-mp="2tovsyot5h15i" />
                    </div>
                    <div data-id="element-2648" data-mp="3rajq5bxfjni3">
                      <input type="tel" name="phone" value={formData.phone} onChange={handleFormChange} required dir="ltr" placeholder="טלפון" className="w-full bg-white/90 border-0 rounded-xl py-3 px-4 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-cyan-400 transition-all text-right" data-id="element-2649" data-mp="3rb364n3is1th" />
                    </div>
                    <div data-id="element-2650" data-mp="25y9wlusid5bc">
                      <input type="email" name="email" value={formData.email} onChange={handleFormChange} dir="ltr" placeholder="אימייל (אופציונלי)" className="w-full bg-white/90 border-0 rounded-xl py-3 px-4 text-gray-900 placeholder:text-gray-500 focus:ring-2 focus:ring-cyan-400 transition-all text-right" data-id="element-2651" data-mp="25yryp81uqdr6" />
                    </div>

                    <button type="submit" disabled={isSubmitting} className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-bold py-4 rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 mt-2" data-id="element-2652" data-mp="1da31bj215uby">
                      {isSubmitting ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-2653" data-mp="20zxddyeizev5"></div> : <>
                          <Send className="w-5 h-5" data-id="element-2654" data-mp="1w6az61spr2x8" />
                          דברו איתי!
                        </>}
                    </button>
                  </form>
                </>}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Benefits */}
      <section className="py-16 bg-gray-50" data-id="element-2655" data-mp="2jwyh7fwcf8xi">
        <div className="container mx-auto px-4" data-id="element-2656" data-mp="6g56fk3ohets">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto" data-id="element-2657" data-mp="6hgazm4y6qly">
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center border border-gray-100" data-id="element-2658" data-mp="bf8e7cb1lz5g">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2659" data-mp="feqpdgd2avv3">
                <Rocket className="w-6 h-6 text-cyan-600" data-id="element-2660" data-mp="kdaaxfnqu5mx" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-2661" data-mp="pdycgv9lm4ve">
                למידה מהירה
              </h3>
              <p className="text-gray-600" data-id="element-2662" data-mp="ubq5jslzbx16">
                עקומת למידה קלה יחסית לגלישת רוח או קייט. תוך מספר שיעורים כבר
                תרגישו את הריחוף.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center border border-gray-100" data-id="element-2663" data-mp="135csct87lpy8">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2664" data-mp="186shawdicglw">
                <ShieldCheck className="w-6 h-6 text-cyan-600" data-id="element-2665" data-mp="1d1y3zxeqhzb1" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-2666" data-mp="gmyivdwpre4k">
                בטיחות מעל הכל
              </h3>
              <p className="text-gray-600" data-id="element-2667" data-mp="lhubog9ry5iq">
                הכנף מוחזקת ביד ולא מחוברת לגוף, מה שהופך את הספורט לבטוח מאוד
                וקל לשליטה.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm text-center border border-gray-100" data-id="element-2668" data-mp="udtkq68souyn">
              <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-2669" data-mp="zbd5lp0005zk">
                <Wind className="w-6 h-6 text-cyan-600" data-id="element-2670" data-mp="14cj4y5o5yri2" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-800" data-id="element-2671" data-mp="19akkwhqamapb">
                תנאים מושלמים
              </h3>
              <p className="text-gray-600" data-id="element-2672" data-mp="1d91ud7x6rx9w">
                רוחות הצהריים היציבות של הכנרת מספקות את התנאים האידיאליים ביותר
                ללמידה.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white" data-id="element-2673" data-mp="37k9xzc78hmcu">
        <div className="container mx-auto px-4" data-id="element-2674" data-mp="37l2y3ubotdm0">
          <div className="max-w-3xl mx-auto" data-id="element-2675" data-mp="32rgvkuz8mq3x">
            <div className="text-center mb-12" data-id="element-2676" data-mp="32qfadxoicn8h">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-50 rounded-full mb-4" data-id="element-2677" data-mp="2xsndep90aaxf">
                <HelpCircle className="w-8 h-8 text-cyan-600" data-id="element-2678" data-mp="2tt4uu04nqbxr" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2679" data-mp="3qicjhh0kp15w">
                כל מה שצריך לדעת
              </h2>
              <p className="text-xl text-gray-600" data-id="element-2680" data-mp="3ljsv79yibdad">
                שאלות ותשובות על הדרכות הווינג פויל שלנו
              </p>
            </div>

            <div className="space-y-6" data-id="element-2681" data-mp="3cil7fltm9uuj">
              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2682" data-mp="3chjokv6d0429">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2683" data-mp="263amnvva9bju">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2684" data-mp="2124ohiiusw2x">
                    1
                  </span>
                  איך בנויה ההדרכה?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2685" data-mp="27hbwqb9r08as">
                  ההדרכה שלנו משלבת חלק תיאורטי ומעשי, ובמהלכה לומדים את יסודות
                  הגלישה עם ווינג בצורה בטוחה וכיפית. משך כל הדרכה הוא כשעה
                  וחצי.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2686" data-mp="1jsixa4tx3ibf">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2687" data-mp="1om51d8m0mdvm">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2688" data-mp="19wz6txy4hbu2">
                    2
                  </span>
                  כמה מפגשים צריך כדי לגלוש לבד?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2689" data-mp="15tt0yrex9mo4">
                  ברוב המקרים, כ־3 מפגשים מספיקים כדי להגיע לרמה שמאפשרת גלישה
                  עצמאית ובטוחה.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2690" data-mp="h9ze4mf397zc">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2691" data-mp="v9gwok1sjp09">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2692" data-mp="r7lz9qmzdvrr">
                    3
                  </span>
                  האם צריך להביא ציוד?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2693" data-mp="1078vly0usbdy">
                  ממש לא! ההדרכה כוללת את כל הציוד הנדרש: ווינג, גלשן פויל,
                  חליפת הצלה וקסדה.
                </p>
              </div>

              <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-100" data-id="element-2694" data-mp="19zy8mxc1xxbe">
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-3" data-id="element-2695" data-mp="1nxw9czxg9v8f">
                  <span className="w-8 h-8 rounded-full bg-cyan-100 text-cyan-700 flex items-center justify-center shrink-0 text-sm" data-id="element-2696" data-mp="1j04jebgd8j6v">
                    4
                  </span>
                  מה כן צריך להביא?
                </h3>
                <p className="text-gray-700 leading-relaxed pr-11" data-id="element-2697" data-mp="3n4jovqly3655">
                  רק בגדים להחלפה ומגבת. אנחנו נדאג לכל השאר.
                </p>
              </div>
            </div>

            <div className="mt-12 text-center bg-cyan-50 rounded-3xl p-8 border border-cyan-100" data-id="element-2698" data-mp="3r7zlwo1xf8id">
              <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-2699" data-mp="2ui8pukdzldag">
                נשמע מעניין?
              </h3>
              <p className="text-lg text-gray-700 mb-6" data-id="element-2700" data-mp="33gu5a6o5wc70">
                השאירו פרטים בטופס למעלה ונקבע לכם מועד שמתאים בדיוק לרוח!
              </p>
              <button onClick={() => window.scrollTo({
              top: 0,
              behavior: 'smooth'
            })} className="bg-cyan-600 hover:bg-cyan-700 text-white font-bold py-3 px-8 rounded-full transition-colors shadow-md" data-id="element-2701" data-mp="38h8gfxl28299">
                חזרה להשארת פרטים ↑
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>;
}